import React from 'react';
import { ChevronDown, Copy, Trash2 } from '../../../bravosIcons';
import type { LibraryTab } from '../../hooks/useLibraryLoader';

export type LibraryMobileBulkActionsProps = {
  activeTab: LibraryTab;
  selectedWorkoutsCount: number;
  selectedWorkoutPlansCount: number;
  isActionsMenuOpen: boolean;
  isWorkoutPlanActionsMenuOpen: boolean;
  actionsMenuRef: React.RefObject<HTMLDivElement>;
  workoutPlanActionsMenuRef: React.RefObject<HTMLDivElement>;
  trainerWorkoutDuplicateInFlight: boolean;
  trainerPlanDuplicateInFlight: boolean;
  onToggleActionsMenu: () => void;
  onTogglePlanActionsMenu: () => void;
  onCloseActionsMenu: () => void;
  onClosePlanActionsMenu: () => void;
  onDuplicateWorkouts: () => void;
  onDeleteWorkouts: () => void;
  onDuplicatePlans: () => void;
  onDeletePlans: () => void;
};

const actionsButtonClass =
  'inline-flex h-11 items-center rounded-2xl border border-slate-900 bg-white px-5 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 dark:border-white/20 dark:bg-transparent dark:text-theme-off-white dark:hover:bg-white/10';

const menuPanelClass =
  'absolute right-0 top-full mt-1 min-w-full bg-white dark:bg-theme-surface rounded-2xl shadow-[0_16px_40px_rgba(15,23,42,0.12)] border border-slate-200 dark:border-white/10 p-2';

function BulkMenuItem({
  label,
  icon: Icon,
  variant,
  disabled,
  onActivate,
}: {
  label: string;
  icon: typeof Copy;
  variant: 'default' | 'danger';
  disabled?: boolean;
  onActivate: () => void;
}) {
  const colorClass =
    variant === 'danger'
      ? 'text-red-600 dark:text-red-400'
      : 'text-theme-text dark:text-theme-off-white';

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (disabled) return;
        onActivate();
      }}
      onTouchEnd={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (disabled) return;
        onActivate();
      }}
      className={`block w-full text-left px-4 py-2 text-sm hover:bg-theme-muted dark:hover:bg-white/10 flex items-center gap-2 ${colorClass}`}
      style={{ touchAction: 'manipulation', pointerEvents: 'auto' }}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

/** Mobile library bulk Actions(N) dropdowns (legacy; desktop uses LibrarySelectionBar). */
export function LibraryMobileBulkActions(props: LibraryMobileBulkActionsProps) {
  const {
    activeTab,
    selectedWorkoutsCount,
    selectedWorkoutPlansCount,
    isActionsMenuOpen,
    isWorkoutPlanActionsMenuOpen,
    actionsMenuRef,
    workoutPlanActionsMenuRef,
    trainerWorkoutDuplicateInFlight,
    trainerPlanDuplicateInFlight,
    onToggleActionsMenu,
    onTogglePlanActionsMenu,
    onCloseActionsMenu,
    onClosePlanActionsMenu,
    onDuplicateWorkouts,
    onDeleteWorkouts,
    onDuplicatePlans,
    onDeletePlans,
  } = props;

  if (activeTab === 'workouts' && selectedWorkoutsCount > 0) {
    return (
      <div className="relative" ref={actionsMenuRef}>
        <button type="button" onClick={(e) => { e.stopPropagation(); onToggleActionsMenu(); }} className={actionsButtonClass}>
          Actions ({selectedWorkoutsCount})
          <ChevronDown className="w-4 h-4" />
        </button>
        {isActionsMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-[100]"
              onClick={onCloseActionsMenu}
              onTouchStart={(e) => e.stopPropagation()}
              style={{ touchAction: 'none' }}
              aria-hidden="true"
            />
            <div className={menuPanelClass} onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} style={{ position: 'absolute', zIndex: 10001 }}>
              <BulkMenuItem label="Duplicate" icon={Copy} variant="default" disabled={trainerWorkoutDuplicateInFlight} onActivate={() => { onCloseActionsMenu(); onDuplicateWorkouts(); }} />
              <BulkMenuItem label="Delete" icon={Trash2} variant="danger" onActivate={() => { onCloseActionsMenu(); onDeleteWorkouts(); }} />
            </div>
          </>
        )}
      </div>
    );
  }

  if (activeTab === 'workoutPlans' && selectedWorkoutPlansCount > 0) {
    return (
      <div className="relative" ref={workoutPlanActionsMenuRef}>
        <button type="button" onClick={(e) => { e.stopPropagation(); onTogglePlanActionsMenu(); }} className={actionsButtonClass}>
          Actions ({selectedWorkoutPlansCount})
          <ChevronDown className="w-4 h-4" />
        </button>
        {isWorkoutPlanActionsMenuOpen && (
          <>
            <div className="fixed inset-0 z-[100]" onClick={onClosePlanActionsMenu} onTouchStart={(e) => e.stopPropagation()} style={{ touchAction: 'none' }} aria-hidden="true" />
            <div
              className={menuPanelClass}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              style={{ position: 'absolute', zIndex: 10001 }}
            >
              <BulkMenuItem label="Duplicate" icon={Copy} variant="default" disabled={trainerPlanDuplicateInFlight} onActivate={() => { onClosePlanActionsMenu(); onDuplicatePlans(); }} />
              <BulkMenuItem label="Delete" icon={Trash2} variant="danger" onActivate={() => { onClosePlanActionsMenu(); onDeletePlans(); }} />
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
}
